import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Tooltip } from "primereact/tooltip";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import CustomDropdown from "../../custom/customDropdown";
import { AuthContext } from "../../Header/auth - context";
import AnalitickaKartica from "./AnalitickaKartica";
import "./Fakture.css";

const toUtcIso = (d) => {
    if (!d) return null;
    const x = d instanceof Date ? d : new Date(d);
    const utc = new Date(x.getTime() - x.getTimezoneOffset() * 60000);
    return utc.toISOString().slice(0, 19) + "Z";
};

const iznos = (v) =>
    v === null || v === undefined || v === ""
        ? "0,00"
        : Number(v).toLocaleString("sr-RS", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const pocetakGodine = () => new Date(new Date().getFullYear(), 0, 1);

/* Zaglavlje tabele po standardu: izvoz levo, pretraga desno. */
const zaglavljeTabele = (izvoz, filter, postaviFilter, tekstPretrage) => (
    <div className="export-buttons fakture-stavke-header">
        <div className="fakture-stavke-radnje">
            <Button type="button" icon="pi pi-file-excel" severity="success" rounded
                onClick={izvoz} data-pr-tooltip="XLS" />
        </div>
        <input type="search" placeholder={tekstPretrage} className="p-inputtext p-component"
            defaultValue={filter || ""} onInput={(e) => postaviFilter(e.target.value)} />
    </div>
);

/* Izvoz je isti posao na sva tri izveštaja — razlikuju se samo zaglavlja i
   redovi, pa se prosleđuju kao podaci, a ne kao tri kopije istog koda. */
const izveziExcel = async (naziv, zaglavlja, redovi, nazivFirme) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(naziv.slice(0, 30));
    const headerRow = worksheet.addRow(zaglavlja);
    headerRow.font = { bold: true };
    headerRow.eachCell((cell) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEFEFEF" } };
        cell.border = { top: { style: "medium" }, left: { style: "medium" }, bottom: { style: "medium" }, right: { style: "medium" } };
    });
    redovi.forEach((r) => {
        const row = worksheet.addRow(r);
        row.eachCell((cell) => {
            cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
        });
    });
    worksheet.columns.forEach((col) => {
        let max = 10;
        col.eachCell((cell) => { if (cell.value) max = Math.max(max, cell.value.toString().length); });
        col.width = max + 2;
    });
    const dt = new Date().toLocaleString("sr-RS", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Belgrade" }).replace(/,/g, "");
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
        `${naziv} - ${nazivFirme || ""} - ${dt}.xlsx`);
};

/* Kategorije stižu iz procedure kao tekst ("1–30", "preko 90"). Klasa se NE
   izvodi iz tog teksta — crtica je en dash, a razmaci i slova bi pri svakoj
   promeni labele tiho razvezali CSS. Zato jedna mapa, koju koriste i kutije
   iznad tabele i boje redova. */
const STAROST = [
    { naziv: "Nedospelo", klasa: "nedospelo" },
    { naziv: "1–30",      klasa: "d30" },
    { naziv: "31–60",     klasa: "d60" },
    { naziv: "61–90",     klasa: "d90" },
    { naziv: "preko 90",  klasa: "preko" },
];

const klasaZa = (naziv) => (STAROST.find((x) => x.naziv === naziv) || {}).klasa;

const klasaStarosti = (r) => {
    const k = klasaZa(r.Starost);
    return k ? { [`iz-red-st-${k}`]: true } : {};
};

// ══════════════════════════ OTVORENE STAVKE (IOS) ══════════════════════════
const OtvoreneStavke = ({ posalji, sifarnici, greska }) => {
    const { t } = useTranslation();
    const company = useSelector((state) => state.company);

    const [redovi, setRedovi] = useState([]);
    const [ucitava, setUcitava] = useState(false);
    const [filter, setFilter] = useState(null);
    const [datumDo, setDatumDo] = useState(new Date());
    const [klijent, setKlijent] = useState(null);
    const [smer, setSmer] = useState("IZLAZ");
    const [samoDospele, setSamoDospele] = useState(false);

    const smerOpcije = [
        { value: "IZLAZ", label: t("izvestaj.smerIzlaz") },
        { value: "ULAZ", label: t("izvestaj.smerUlaz") },
    ];

    const ucitaj = () => {
        setUcitava(true);
        posalji("IzvestajOtvorene", {
            datumDo: toUtcIso(datumDo), id_klijent: klijent, smer,
            samoDospele: samoDospele ? 1 : 0,
        })
            .then((d) => setRedovi(d || []))
            .catch(greska)
            .finally(() => setUcitava(false));
    };

    useEffect(() => { ucitaj(); /* eslint-disable-next-line */ }, []);

    const zbir = useMemo(() => redovi.reduce((s, r) => s + Number(r.Otvoreno || 0), 0), [redovi]);

    /* Starosna struktura je poenta izveštaja — bez zbira po kategoriji spisak
       kaže ko duguje, ali ne i koliko je duga zaista staro. */
    const poStarosti = useMemo(() => {
        const m = {};
        redovi.forEach((r) => { m[r.Starost] = (m[r.Starost] || 0) + Number(r.Otvoreno || 0); });
        return STAROST.filter((x) => m[x.naziv])
                      .map((x) => ({ ...x, iznos: m[x.naziv] }));
    }, [redovi]);

    const izvoz = () => izveziExcel(
        t("izvestaj.tabOtvorene"),
        [t("izvod.klijent"), "PIB", t("izvod.brojFakture"), t("izvestaj.smer"), t("izvod.datum"),
         t("ulazne.rokPlacanja"), t("ulazne.valuta"), t("izvestaj.bruto"), t("izvestaj.kokz"),
         t("izvestaj.placeno"), t("izvestaj.otvoreno"), t("izvestaj.danaKasni"), t("izvestaj.starost")],
        redovi.map((r) => [r.Klijent, r.PIB || "", r.Broj, r.Smer, r.Datum, r.Rok, r.Valuta || "",
            Number(r.Bruto || 0), Number(r.KoKz || 0), Number(r.Placeno || 0), Number(r.Otvoreno || 0),
            Number(r.DanaKasni || 0), r.Starost]),
        company?.companyName);

    const footer = (
        <ColumnGroup>
            <Row>
                <Column footer={t("izvod.ukupno")} colSpan={9} footerStyle={{ textAlign: "right" }} />
                <Column footer={iznos(zbir)} footerStyle={{ textAlign: "right" }} />
                <Column footer="" colSpan={2} />
            </Row>
        </ColumnGroup>
    );

    return (
        <div className="col-12">
            <div className="faktureStranica">
                <Tooltip target=".export-buttons>button" position="bottom" />
                <Card>
                    <div className="sef-ulazne-filter">
                        <div className="sef-filter-polje">
                            <label>{t("izvestaj.presek")}:</label>
                            <Calendar value={datumDo} dateFormat="dd.mm.yy." showIcon
                                onChange={(e) => setDatumDo(e.value)} />
                        </div>
                        <div className="sef-filter-polje">
                            <label>{t("izvestaj.smer")}:</label>
                            <CustomDropdown value={smer} optionLabel="label" options={smerOpcije}
                                onChange={(e) => setSmer(e.target.value)} />
                        </div>
                        <div className="sef-filter-polje">
                            <label>{t("izvod.klijent")}:</label>
                            <CustomDropdown value={klijent} optionLabel="label" showClear filter
                                options={sifarnici.KlijentiDB || []}
                                placeholder={t("izvod.sviKlijenti")}
                                onChange={(e) => setKlijent(e.target.value)} />
                        </div>
                        <div className="sef-filter-polje iz-kvacica">
                            <label>{t("izvestaj.samoDospele")}:</label>
                            <Checkbox inputId="izv-dospele" checked={samoDospele}
                                onChange={(e) => setSamoDospele(e.checked)} />
                        </div>
                        <div className="sef-filter-dugmad">
                            <Button type="button" label={t("sefUlazne.ucitaj")} icon="pi pi-search"
                                className="p-button-sm" loading={ucitava} onClick={ucitaj} />
                        </div>
                    </div>
                </Card>

                {poStarosti.length > 0 && (
                    <Card>
                        <div className="izv-starost">
                            {poStarosti.map((s) => (
                                <div key={s.klasa} className={`izv-starost-kutija ${s.klasa}`}>
                                    <span className="izv-starost-naslov">{s.naziv}</span>
                                    <strong className="ak-iznos">{iznos(s.iznos)}</strong>
                                </div>
                            ))}
                        </div>
                    </Card>
                )}

                <Card>
                    <DataTable value={redovi} loading={ucitava}
                        paginator rows={25} rowsPerPageOptions={[25, 50, 100]}
                        globalFilter={filter} header={zaglavljeTabele(izvoz, filter, setFilter, t("nova.phPretraga"))}
                        scrollable scrollHeight="55vh" size="small" showGridlines
                        resizableColumns columnResizeMode="expand"
                        emptyMessage={t("common.noDataTable")}
                        className="turePregledTable stavke-tabela" rowClassName={klasaStarosti}
                        footerColumnGroup={footer}>
                        <Column field="Klijent" header={t("izvod.klijent")} style={{ minWidth: "220px" }} />
                        <Column field="PIB" header="PIB" style={{ minWidth: "120px" }} />
                        <Column field="Broj" header={t("izvod.brojFakture")} style={{ minWidth: "130px" }} />
                        <Column field="Smer" header={t("izvestaj.smer")} style={{ minWidth: "100px" }} />
                        <Column field="Datum" header={t("izvod.datum")} style={{ minWidth: "110px" }} />
                        <Column field="Rok" header={t("ulazne.rokPlacanja")} style={{ minWidth: "110px" }} />
                        <Column field="Valuta" header={t("ulazne.valuta")} style={{ minWidth: "90px" }} />
                        <Column header={t("izvestaj.bruto")} style={{ minWidth: "120px" }}
                            body={(r) => <span className="ak-iznos">{iznos(r.Bruto)}</span>} />
                        <Column header={t("izvestaj.kokz")} style={{ minWidth: "110px" }}
                            body={(r) => <span className="ak-iznos">{iznos(r.KoKz)}</span>} />
                        <Column header={t("izvestaj.otvoreno")} style={{ minWidth: "130px" }}
                            body={(r) => <strong className="ak-iznos">{iznos(r.Otvoreno)}</strong>} />
                        <Column field="DanaKasni" header={t("izvestaj.danaKasni")} style={{ minWidth: "110px" }} />
                        <Column field="Starost" header={t("izvestaj.starost")} style={{ minWidth: "110px" }} />
                    </DataTable>
                </Card>
            </div>
        </div>
    );
};

// ══════════════════════════ KONTROLA SRAVNJENJA ══════════════════════════
const KontrolaSravnjenja = ({ posalji, sifarnici, greska }) => {
    const { t } = useTranslation();
    const company = useSelector((state) => state.company);

    const [redovi, setRedovi] = useState([]);
    const [ucitava, setUcitava] = useState(false);
    const [filter, setFilter] = useState(null);
    const [klijent, setKlijent] = useState(null);
    const [datumOd, setDatumOd] = useState(pocetakGodine());
    const [datumDo, setDatumDo] = useState(new Date());

    const ucitaj = () => {
        setUcitava(true);
        posalji("IzvestajKontrola", {
            id_klijent: klijent, datumOd: toUtcIso(datumOd), datumDo: toUtcIso(datumDo),
        })
            .then((d) => setRedovi(d || []))
            .catch(greska)
            .finally(() => setUcitava(false));
    };

    useEffect(() => { ucitaj(); /* eslint-disable-next-line */ }, []);

    const izvoz = () => izveziExcel(
        t("izvestaj.tabKontrola"),
        [t("izvod.brojFakture"), t("izvod.klijent"), t("izvod.datum"), t("izvestaj.bruto"),
         t("izvestaj.placenoNaFakturi"), t("izvestaj.zbirVeza"), t("izvestaj.razlika"),
         t("izvestaj.brojVeza"), t("izvestaj.opisRazlike")],
        redovi.map((r) => [r.Broj, r.Klijent, r.Datum, Number(r.Bruto || 0),
            Number(r.PlacenoNaFakturi || 0), Number(r.ZbirVeza || 0), Number(r.Razlika || 0),
            r.BrojVeza, r.Opis]),
        company?.companyName);

    return (
        <div className="col-12">
            <div className="faktureStranica">
                <Tooltip target=".export-buttons>button" position="bottom" />
                <Card>
                    <div className="sef-ulazne-napomena" style={{ marginTop: 0 }}>
                        <i className="pi pi-info-circle" /> {t("izvestaj.kontrolaOpis")}
                    </div>
                    <div className="sef-ulazne-filter">
                        <div className="sef-filter-polje">
                            <label>{t("izvestaj.datumOd")}:</label>
                            <Calendar value={datumOd} dateFormat="dd.mm.yy." showIcon
                                onChange={(e) => setDatumOd(e.value)} />
                        </div>
                        <div className="sef-filter-polje">
                            <label>{t("izvestaj.datumDo")}:</label>
                            <Calendar value={datumDo} dateFormat="dd.mm.yy." showIcon
                                onChange={(e) => setDatumDo(e.value)} />
                        </div>
                        <div className="sef-filter-polje">
                            <label>{t("izvod.klijent")}:</label>
                            <CustomDropdown value={klijent} optionLabel="label" showClear filter
                                options={sifarnici.KlijentiDB || []}
                                placeholder={t("izvod.sviKlijenti")}
                                onChange={(e) => setKlijent(e.target.value)} />
                        </div>
                        <div className="sef-filter-dugmad">
                            <Button type="button" label={t("sefUlazne.ucitaj")} icon="pi pi-search"
                                className="p-button-sm" loading={ucitava} onClick={ucitaj} />
                        </div>
                    </div>
                </Card>

                <Card>
                    {!ucitava && redovi.length === 0 && (
                        <div className="sef-traka ok">
                            <i className="pi pi-check-circle" /> {t("izvestaj.kontrolaCisto")}
                        </div>
                    )}
                    <DataTable value={redovi} loading={ucitava}
                        paginator rows={25} rowsPerPageOptions={[25, 50, 100]}
                        globalFilter={filter} header={zaglavljeTabele(izvoz, filter, setFilter, t("nova.phPretraga"))}
                        scrollable scrollHeight="55vh" size="small" showGridlines
                        resizableColumns columnResizeMode="expand"
                        emptyMessage={t("izvestaj.kontrolaCisto")}
                        className="turePregledTable stavke-tabela"
                        rowClassName={() => ({ "iz-red-nesaglasno": true })}>
                        <Column field="Broj" header={t("izvod.brojFakture")} style={{ minWidth: "130px" }} />
                        <Column field="Klijent" header={t("izvod.klijent")} style={{ minWidth: "220px" }} />
                        <Column field="Datum" header={t("izvod.datum")} style={{ minWidth: "110px" }} />
                        <Column header={t("izvestaj.bruto")} style={{ minWidth: "120px" }}
                            body={(r) => <span className="ak-iznos">{iznos(r.Bruto)}</span>} />
                        <Column header={t("izvestaj.placenoNaFakturi")} style={{ minWidth: "150px" }}
                            body={(r) => <span className="ak-iznos">{iznos(r.PlacenoNaFakturi)}</span>} />
                        <Column header={t("izvestaj.zbirVeza")} style={{ minWidth: "130px" }}
                            body={(r) => <span className="ak-iznos">{iznos(r.ZbirVeza)}</span>} />
                        <Column header={t("izvestaj.razlika")} style={{ minWidth: "130px" }}
                            body={(r) => <strong className="ak-iznos ak-minus">{iznos(r.Razlika)}</strong>} />
                        <Column field="BrojVeza" header={t("izvestaj.brojVeza")} style={{ minWidth: "100px" }} />
                        <Column field="Opis" header={t("izvestaj.opisRazlike")} style={{ minWidth: "220px" }} />
                    </DataTable>
                </Card>
            </div>
        </div>
    );
};

// ══════════════════════════ PROMET PO PARTNERIMA ══════════════════════════
const PrometPartnera = ({ posalji, sifarnici, greska }) => {
    const { t } = useTranslation();
    const company = useSelector((state) => state.company);

    const [redovi, setRedovi] = useState([]);
    const [ucitava, setUcitava] = useState(false);
    const [filter, setFilter] = useState(null);
    const [datumOd, setDatumOd] = useState(pocetakGodine());
    const [datumDo, setDatumDo] = useState(new Date());
    const [valuta, setValuta] = useState(null);

    const ucitaj = () => {
        setUcitava(true);
        posalji("IzvestajPromet", {
            datumOd: toUtcIso(datumOd), datumDo: toUtcIso(datumDo), valuta,
        })
            .then((d) => setRedovi(d || []))
            .catch(greska)
            .finally(() => setUcitava(false));
    };

    useEffect(() => { ucitaj(); /* eslint-disable-next-line */ }, []);

    const zbir = useMemo(() => ({
        izlazno: redovi.reduce((s, r) => s + Number(r.Izlazno || 0), 0),
        ulazno: redovi.reduce((s, r) => s + Number(r.Ulazno || 0), 0),
        potrazujemo: redovi.reduce((s, r) => s + Number(r.PotrazujemoOtvoreno || 0), 0),
        dugujemo: redovi.reduce((s, r) => s + Number(r.DugujemoOtvoreno || 0), 0),
    }), [redovi]);

    const izvoz = () => izveziExcel(
        t("izvestaj.tabPromet"),
        [t("izvod.klijent"), "PIB", t("izvestaj.izlazno"), t("izvestaj.ulazno"),
         t("izvestaj.naplaceno"), t("izvestaj.isplaceno"),
         t("izvestaj.potrazujemo"), t("izvestaj.dugujemo"), t("izvestaj.brojDokumenata")],
        redovi.map((r) => [r.Klijent, r.PIB || "", Number(r.Izlazno || 0), Number(r.Ulazno || 0),
            Number(r.Naplaceno || 0), Number(r.Isplaceno || 0),
            Number(r.PotrazujemoOtvoreno || 0), Number(r.DugujemoOtvoreno || 0), r.BrojDokumenata]),
        company?.companyName);

    const footer = (
        <ColumnGroup>
            <Row>
                <Column footer={t("izvod.ukupno")} colSpan={2} footerStyle={{ textAlign: "right" }} />
                <Column footer={iznos(zbir.izlazno)} footerStyle={{ textAlign: "right" }} />
                <Column footer={iznos(zbir.ulazno)} footerStyle={{ textAlign: "right" }} />
                <Column footer="" colSpan={2} />
                <Column footer={iznos(zbir.potrazujemo)} footerStyle={{ textAlign: "right" }} />
                <Column footer={iznos(zbir.dugujemo)} footerStyle={{ textAlign: "right" }} />
                <Column footer="" />
            </Row>
        </ColumnGroup>
    );

    return (
        <div className="col-12">
            <div className="faktureStranica">
                <Tooltip target=".export-buttons>button" position="bottom" />
                <Card>
                    <div className="sef-ulazne-filter">
                        <div className="sef-filter-polje">
                            <label>{t("izvestaj.datumOd")}:</label>
                            <Calendar value={datumOd} dateFormat="dd.mm.yy." showIcon
                                onChange={(e) => setDatumOd(e.value)} />
                        </div>
                        <div className="sef-filter-polje">
                            <label>{t("izvestaj.datumDo")}:</label>
                            <Calendar value={datumDo} dateFormat="dd.mm.yy." showIcon
                                onChange={(e) => setDatumDo(e.value)} />
                        </div>
                        <div className="sef-filter-polje">
                            <label>{t("ulazne.valuta")}:</label>
                            <CustomDropdown value={valuta} optionLabel="label" showClear
                                options={sifarnici.ValuteDB || []}
                                placeholder={t("izvestaj.sveValute")}
                                onChange={(e) => setValuta(e.target.value)} />
                        </div>
                        <div className="sef-filter-dugmad">
                            <Button type="button" label={t("sefUlazne.ucitaj")} icon="pi pi-search"
                                className="p-button-sm" loading={ucitava} onClick={ucitaj} />
                        </div>
                    </div>
                </Card>

                <Card>
                    <DataTable value={redovi} loading={ucitava}
                        paginator rows={25} rowsPerPageOptions={[25, 50, 100]}
                        globalFilter={filter} header={zaglavljeTabele(izvoz, filter, setFilter, t("nova.phPretraga"))}
                        scrollable scrollHeight="55vh" size="small" showGridlines
                        resizableColumns columnResizeMode="expand"
                        emptyMessage={t("common.noDataTable")}
                        className="turePregledTable stavke-tabela" footerColumnGroup={footer}>
                        <Column field="Klijent" header={t("izvod.klijent")} style={{ minWidth: "220px" }} />
                        <Column field="PIB" header="PIB" style={{ minWidth: "120px" }} />
                        <Column header={t("izvestaj.izlazno")} style={{ minWidth: "130px" }}
                            body={(r) => <span className="ak-iznos">{iznos(r.Izlazno)}</span>} />
                        <Column header={t("izvestaj.ulazno")} style={{ minWidth: "130px" }}
                            body={(r) => <span className="ak-iznos">{iznos(r.Ulazno)}</span>} />
                        <Column header={t("izvestaj.naplaceno")} style={{ minWidth: "130px" }}
                            body={(r) => <span className="ak-iznos">{iznos(r.Naplaceno)}</span>} />
                        <Column header={t("izvestaj.isplaceno")} style={{ minWidth: "130px" }}
                            body={(r) => <span className="ak-iznos">{iznos(r.Isplaceno)}</span>} />
                        <Column header={t("izvestaj.potrazujemo")} style={{ minWidth: "140px" }}
                            body={(r) => <strong className="ak-iznos ak-plus">{iznos(r.PotrazujemoOtvoreno)}</strong>} />
                        <Column header={t("izvestaj.dugujemo")} style={{ minWidth: "140px" }}
                            body={(r) => <strong className="ak-iznos ak-minus">{iznos(r.DugujemoOtvoreno)}</strong>} />
                        <Column field="BrojDokumenata" header={t("izvestaj.brojDokumenata")} style={{ minWidth: "110px" }} />
                    </DataTable>
                </Card>
            </div>
        </div>
    );
};

// ══════════════════════════ KONTEJNER ══════════════════════════
const Izvestaji = () => {
    const { t } = useTranslation();
    const auth = useContext(AuthContext);
    const toast = useRef(null);

    const [tab, setTab] = useState("KARTICA");
    const [sifarnici, setSifarnici] = useState({});

    const posalji = async (putanja, telo = {}) => {
        const r = await fetch(process.env.REACT_APP_BACKEND_URL + "/izvod/" + putanja, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
            body: JSON.stringify(telo),
        });
        const tekst = await r.text();
        const d = tekst ? JSON.parse(tekst) : {};
        if (!r.ok) throw new Error(d.message || t("common.greska"));
        return d;
    };

    const greska = (e) =>
        toast.current?.show({ severity: "error", summary: t("common.greska"), detail: e.message, life: 5000 });

    /* Šifarnici se učitavaju jednom za sve izveštaje — svaki bi inače povlačio
       isti spisak klijenata pri svakom prelasku na tab. */
    useEffect(() => {
        posalji("IzvestajInit").then(setSifarnici).catch(() => {});
        // eslint-disable-next-line
    }, []);

    const tabovi = [
        { kljuc: "KARTICA", naziv: t("izvestaj.tabKartica") },
        { kljuc: "OTVORENE", naziv: t("izvestaj.tabOtvorene") },
        { kljuc: "PROMET", naziv: t("izvestaj.tabPromet") },
        { kljuc: "KONTROLA", naziv: t("izvestaj.tabKontrola") },
    ];

    /* Trakica sa karticama je ista kao na ulaznim fakturama (Nova dinarska /
       Nova devizna / Pregled faktura): PrimeReact dugmad u
       .ulazne-trakica-kartica, a aktivno nosi id="activeButton". Taj id je
       nosilac stila kroz celu aplikaciju — i tamna tema ga hvata preko njega,
       pa sopstvena traka tabova ovde ne bi bila obojena kako treba. */
    return (
        <div className="Izvestaji_fakture">
            <Toast ref={toast} />
            <div className="ulazne-trakica-kartica">
                {tabovi.map((x) => (
                    <Button key={x.kljuc} label={x.naziv}
                        id={tab === x.kljuc ? "activeButton" : undefined}
                        style={{ fontSize: "smaller" }}
                        className="mt-3 zaposleniShift"
                        onClick={() => setTab(x.kljuc)} />
                ))}
            </div>

            {/* Analitička kartica je zaseban ekran sa svojim učitavanjem — ovde
                stoji kao prvi izveštaj, nepromenjena. */}
            {tab === "KARTICA" && <AnalitickaKartica />}
            {tab === "OTVORENE" && <OtvoreneStavke posalji={posalji} sifarnici={sifarnici} greska={greska} />}
            {tab === "PROMET" && <PrometPartnera posalji={posalji} sifarnici={sifarnici} greska={greska} />}
            {tab === "KONTROLA" && <KontrolaSravnjenja posalji={posalji} sifarnici={sifarnici} greska={greska} />}
        </div>
    );
};

export default Izvestaji;
